/**
 * Anlegen/Bearbeiten einer Honorarkraft – Einzelperson oder über eine Firma,
 * mit Funktionen (Tags) und mehreren Einsatz-Standorten.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, X } from 'lucide-react';
import { honorarkraefteAPI } from '../../lib/api';
import { toast } from 'sonner';

const EMPTY = {
  type: 'einzelperson', first_name: '', last_name: '', firma_id: '',
  email: '', telefon: '', mobil: '', positions: [], location_ids: [],
  strasse: '', hausnummer: '', plz: '', ort: '',
  steuernummer: '', ust_idnr: '', notes: '', is_active: true,
};

export const HonorarkraftDialog = ({ open, onClose, onSaved, editData, firmen, locations }) => {
  const [form, setForm] = useState(EMPTY);
  const [posInput, setPosInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editData ? { ...EMPTY, ...editData, firma_id: editData.firma_id || '' } : EMPTY);
      setPosInput('');
    }
  }, [open, editData]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addPos = () => {
    const v = posInput.trim();
    if (v && !form.positions.includes(v)) set('positions', [...form.positions, v]);
    setPosInput('');
  };

  const toggleLoc = (id) => {
    set('location_ids', form.location_ids.includes(id)
      ? form.location_ids.filter((l) => l !== id)
      : [...form.location_ids, id]);
  };

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast.error('Bitte Vor- und Nachname angeben');
      return;
    }
    if (form.type === 'firma' && !form.firma_id) {
      toast.error('Bitte eine Firma auswählen');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, firma_id: form.type === 'firma' ? form.firma_id : null };
      if (editData) await honorarkraefteAPI.update(editData.id, payload);
      else await honorarkraefteAPI.create(payload);
      toast.success('Honorarkraft gespeichert');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="honorarkraft-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editData ? 'Honorarkraft bearbeiten' : 'Honorarkraft anlegen'}</DialogTitle>
          <DialogDescription className="sr-only">Stammdaten einer Honorarkraft erfassen.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Art</Label>
            <RadioGroup value={form.type} onValueChange={(v) => set('type', v)} className="flex gap-6">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="einzelperson" id="hk-type-einzel" data-testid="hk-type-einzelperson" />
                <Label htmlFor="hk-type-einzel" className="font-normal cursor-pointer">Einzelperson</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="firma" id="hk-type-firma" data-testid="hk-type-firma" />
                <Label htmlFor="hk-type-firma" className="font-normal cursor-pointer">Über eine Firma</Label>
              </div>
            </RadioGroup>
          </div>

          {form.type === 'firma' && (
            <div className="space-y-2">
              <Label>Firma</Label>
              <Select value={form.firma_id} onValueChange={(v) => set('firma_id', v)}>
                <SelectTrigger data-testid="hk-firma-select"><SelectValue placeholder="Firma wählen" /></SelectTrigger>
                <SelectContent>
                  {firmen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>))}
                </SelectContent>
              </Select>
              {firmen.length === 0 && <p className="text-xs text-muted-foreground">Noch keine Firmen angelegt – bitte zuerst im Tab „Firmen" anlegen.</p>}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{form.type === 'firma' ? 'Vorname (Ansprechpartner)' : 'Vorname'}</Label>
              <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="hk-first-name" />
            </div>
            <div className="space-y-2">
              <Label>{form.type === 'firma' ? 'Nachname (Ansprechpartner)' : 'Nachname'}</Label>
              <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="hk-last-name" />
            </div>
            <div className="space-y-2">
              <Label>E-Mail</Label>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="hk-email" />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input value={form.telefon} onChange={(e) => set('telefon', e.target.value)} data-testid="hk-telefon" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-2 sm:col-span-2">
              <Label>Straße</Label>
              <Input value={form.strasse} onChange={(e) => set('strasse', e.target.value)} data-testid="hk-strasse" />
            </div>
            <div className="space-y-2">
              <Label>Nr.</Label>
              <Input value={form.hausnummer} onChange={(e) => set('hausnummer', e.target.value)} data-testid="hk-hausnummer" />
            </div>
            <div className="space-y-2">
              <Label>PLZ</Label>
              <Input value={form.plz} onChange={(e) => set('plz', e.target.value)} data-testid="hk-plz" />
            </div>
            <div className="space-y-2 sm:col-span-4">
              <Label>Ort</Label>
              <Input value={form.ort} onChange={(e) => set('ort', e.target.value)} data-testid="hk-ort" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Funktionen</Label>
            <div className="flex gap-2">
              <Input
                value={posInput}
                onChange={(e) => setPosInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addPos(); } }}
                placeholder="z. B. Dozent, Job-Coach – Enter zum Hinzufügen"
                data-testid="hk-position-input"
              />
              <Button type="button" variant="outline" onClick={addPos}>Hinzufügen</Button>
            </div>
            {form.positions.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {form.positions.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary" data-testid={`hk-position-tag-${p}`}>
                    {p}
                    <button type="button" onClick={() => set('positions', form.positions.filter((x) => x !== p))}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Einsatz-Standorte</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto rounded-md border p-3">
              {locations.map((l) => (
                <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={form.location_ids.includes(l.id)} onCheckedChange={() => toggleLoc(l.id)} data-testid={`hk-location-${l.id}`} />
                  {l.name}
                </label>
              ))}
              {locations.length === 0 && <p className="text-xs text-muted-foreground">Keine Standorte verfügbar.</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Steuernummer</Label>
              <Input value={form.steuernummer} onChange={(e) => set('steuernummer', e.target.value)} data-testid="hk-steuernummer" />
            </div>
            <div className="space-y-2">
              <Label>USt-IdNr.</Label>
              <Input value={form.ust_idnr} onChange={(e) => set('ust_idnr', e.target.value)} data-testid="hk-ustid" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notizen</Label>
            <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} data-testid="hk-notes" />
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={(v) => set('is_active', v)} id="hk-active" data-testid="hk-active" />
            <Label htmlFor="hk-active" className="font-normal">Aktiv</Label>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" className="rounded-full" onClick={onClose} disabled={saving}>Abbrechen</Button>
            <Button className="rounded-full" onClick={save} disabled={saving} data-testid="save-honorarkraft-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Speichern'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
